/**
 * Presentation helpers for the game table.
 *
 * Kept out of the components so the arithmetic — seat positions, bet sizing steps,
 * card names — can be tested without rendering anything, and so a component never
 * quietly grows a second opinion about how a number should be formatted.
 */
import { RANKS, SUITS } from "@pipeline/solve/evaluator";
import type { Action } from "@engine/types";
import { randomSeed, seedFromLabel, toHex } from "@engine/rng";

export interface SeatBox { x: number; y: number; betX: number; betY: number }

/**
 * Seats placed around an ellipse with the hero at the bottom centre.
 *
 * The hero is always at six o'clock regardless of which seat index they occupy,
 * because that is where a player sits at a real table and every online client puts
 * them. Rotating the ARRAY instead of the picture would break the seat indices that
 * the engine, the history and the reads all key off.
 */
/*
 * PHASE 9: THE CLASSIC FELT'S RING IS RETIRED WITH IT. `COMPACT_FROM`, the 1:1.762
 * portrait felt's centre and radii, its board band and `SEAT_HALF` described `GameTable`,
 * which was the only caller of `seatPositions()` without a ring. The kit's two rings
 * below are the only rings now, and a ring is a required argument.
 */

/**
 * The ring a set of seats is placed on.
 *
 * ===================== WHY THIS IS A PARAMETER NOW =============================
 * The top-down table (Phase 2) puts seats on a DIFFERENT plate: a 1.389:1 oval that
 * rotates for portrait, not the 1:1.762 portrait felt this file was written for. The
 * mock that was approved does its own `seatPoints()` in `source/build-topdown.py`,
 * and copying that into the client would have been a second opinion about where a
 * seat goes — the exact thing this module exists to prevent.
 *
 * So the maths stays here, once, and the numbers that differ between the two tables
 * become a ring. (Phase 9: the classic felt is retired, so the kit's rings are the only
 * two, and the classic values quoted below are history — they say why each field is
 * what it is on the kit.)
 * ===============================================================================
 */
export interface SeatRing {
  /** Centre and radii, as percentages of the box the seats are placed in. */
  cx: number; cy: number; rx: number; ry: number;
  /**
   * Height ÷ width of that box.
   *
   * Only used to measure DISTANCE while walking the ellipse: y is a percentage of a
   * different axis from x, so a table taller than it is wide needs y scaled up before
   * two points can be compared. The classic felt is 1.762; the top-down plate's seat
   * ring is walked at 1, which is what the mock does and what its renders were
   * approved from.
   */
  aspect: number;
  /**
   * How much of the rail the hero occupies, in seats.
   *
   * 1.0 on the classic felt — see HERO_GAP below, where 1.35 was tried and rejected.
   * 2.1 on the top-down plate, where it is doing a different job: pushing seats 2 and
   * n off the bottom edge, which on that plate is where the hero zone lives.
   *
   * It was 1.7, which cleared the bottom of a felt with the hero zone UNDER it and not
   * one with the hero zone OVER it. In the landscape layout the zone is an overlay
   * sitting on the bottom-left of the plate, and at nine-handed the seat at eight
   * o'clock was under the hero's own two cards — a hand covering an opponent's stack,
   * on a laptop and on a phone held sideways. 2.1 seats of gap puts the nearest seat
   * clear of it; the others spread a little further round the top of the ring, which is
   * where the room is. 2.5 was tried first and is too much: at nine-handed on a 346 x
   * 249 felt the seats it pushed round the top ran into each other.
   */
  heroGap: number;
  /**
   * A band across the middle no seat may stand in: its centre and half-height, or
   * null for none.
   *
   * The classic felt's board is 85.6% of the table's width and crosses it edge to
   * edge, so there is no "beside the board". The top-down plate is wide rather than
   * tall and its board spans about a third of the felt, so there is room either side
   * and the band is off.
   */
  board: { centre: number; half: number } | null;
  /** How far in from the seat the bet chip sits, as a fraction of its offset. */
  betPull: { x: number; y: number };
}

/**
 * The top-down plate's seat ring — MEASURED OFF THE ART, not taken from the mock.
 *
 * `source/build-topdown.py` walks 0.46 x 0.46 in landscape and 0.47 x 0.44 in
 * portrait, and the portrait pair is wrong. The plate rotates a quarter turn in
 * portrait; the radii did not rotate with it. So the ring was about two percent too
 * wide and two percent too short for the table it was drawn on, and on a 390px phone
 * that put the nine- and three-o'clock seats off the wood altogether — a folded player
 * floating on the background beside the table, which is what Tim caught in the
 * screenshots.
 *
 * The numbers below come from `public/art/table/poker-topdown-1100.webp` instead. The
 * green felt's semi-axes are 42.59% x 40.59% of that image and the wood's outer edge is
 * 49.45% x 49.18%, so the rail's midline is 46.02% x 44.89% and a seat sits on it.
 * Portrait is that pair with the axes swapped, because that is what rotating a picture
 * does. `TOPDOWN_RAIL` below carries the band, and the harness asserts every seat lands
 * inside it on every combination it sweeps.
 *
 * The BET ring keeps the mock's absolute radii — 33 x 30 landscape, 33 x 29 portrait —
 * expressed as a fraction of the new seat radii, so the chips have not moved. It is a
 * pull along the seat's own bearing rather than the mock's second ellipse, which is
 * what the classic felt already does and what stops a chip drifting onto a neighbour's
 * line.
 *
 * Both rings are exported because the component emits BOTH sets of coordinates and lets
 * a container query choose — orientation is CSS on that table, never a prop.
 */
export const TOPDOWN_RING_LANDSCAPE: SeatRing = {
  cx: 50, cy: 50, rx: 46.02, ry: 44.89,
  aspect: 1,
  heroGap: 2.1,
  board: null,
  betPull: { x: 33 / 46.02, y: 30 / 44.89 },
};

export const TOPDOWN_RING_PORTRAIT: SeatRing = {
  cx: 50, cy: 50, rx: 44.89, ry: 46.02,
  aspect: 1,
  heroGap: 2.1,
  board: null,
  betPull: { x: 33 / 44.89, y: 29 / 46.02 },
};

/**
 * Where the rail actually is, measured off the plate art rather than guessed.
 *
 * Kept beside the rings because it is what they are derived from, and because a
 * different plate means different numbers. `harness/_shottable.mjs` asserts every seat
 * centre lands between `felt` and `wood` on every combination it sweeps.
 *
 * From `public/art/table/poker-topdown-1100.webp`, as percentages of the image:
 *   felt (green) semi-axes  42.59 x 40.59
 *   wood outer semi-axes    49.45 x 49.18
 * The rail's midline is the average of the two.
 */
export const TOPDOWN_RAIL = {
  felt: { x: 42.59, y: 40.59 },
  wood: { x: 49.45, y: 49.18 },
} as const;

const cache = new Map<string, SeatBox[]>();

/** A ring's identity for the cache. Cheap, and it cannot collide across rings. */
const ringKey = (r: SeatRing): string =>
  `${r.cx},${r.cy},${r.rx},${r.ry},${r.aspect},${r.heroGap},` +
  `${r.board ? `${r.board.centre}/${r.board.half}` : "-"},${r.betPull.x},${r.betPull.y}`;

/**
 * Seats placed around the felt with the hero at the bottom centre.
 *
 * The hero is always at six o'clock regardless of which seat index they occupy,
 * because that is where a player sits at a real table and every online client puts
 * them. Rotating the ARRAY instead of the picture would break the seat indices that
 * the engine, the history and the reads all key off.
 *
 * Two properties are worth stating because both were arrived at by getting them
 * wrong first, and neither is obvious from reading the code:
 *
 * 1. Seats are spaced by ARC LENGTH, not by angle. On a table 1.762 times taller
 *    than it is wide, equal angles are nowhere near equal distances — near the top
 *    and bottom the ellipse turns quickly and evenly-spaced angles bunch seats
 *    almost on top of each other. Measured in real distance, they space evenly.
 *
 * 2. The board's band is excluded outright. The board is 85.6% of the table's width,
 *    so it crosses the felt edge to edge: there is no "beside the board", only above
 *    or below. The excluded band is widened by the seat's own half-height, which is
 *    why the compact seat can use arcs the full one cannot.
 */
export function seatPositions(count: number, heroSeat: number, ring: SeatRing): SeatBox[] {
  if (count < 2 || count > 9) throw new Error(`a table seats 2 to 9, not ${count}`);
  const hero = heroSeat >= 0 ? heroSeat : 0;
  const key = `${count}:${hero}:${ringKey(ring)}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const r: SeatRing = ring;

  /**
   * Walk the ellipse from the hero's position, accumulating length only where a seat
   * may actually stand. Sampled rather than solved: an elliptic arc length has no
   * closed form, and at this resolution the error is far below a pixel.
   */
  const N = 2000;
  const pts: { x: number; y: number; ok: boolean }[] = [];
  for (let k = 0; k < N; k++) {
    const a = Math.PI / 2 + (2 * Math.PI * k) / N;
    /**
     * ============ PHASE 5, BOX 0(a): THE WALK GOES CLOCKWISE ===================
     * `+ cos`, not `- cos`, and that one character is the direction the game runs.
     *
     * The walk starts at the hero (a = pi/2 puts the first point at cx, cy + ry —
     * the bottom of the ellipse, which on screen is six o'clock) and every seat
     * after them is placed further along it. So the direction this loop walks IS
     * the order of action: seat `hero + 1` lands on the first point after the hero.
     *
     * With `- cos` the walk left the hero to the RIGHT — 6 o'clock, 3, 12, 9 — so
     * the player who acted after you sat on your screen-right and the button hopped
     * anticlockwise. Tim caught it in the first real sitting on preview, and it is
     * not a cosmetic complaint: from directly above, every seat faces the middle, so
     * the neighbour on your LEFT is the one at screen-left, and poker action moves
     * to each player's left. 6 -> 7 -> 8 -> 9 o'clock. Clockwise, as seen from
     * above, which is what a real table does and what every other client draws.
     *
     * `+ cos` mirrors the walk about x = cx. All three rings are centred on cx = 50,
     * so the arc-length spacing, the rail clearances and every geometric assertion in
     * `table-ui.test.ts` and the harness sweep are unchanged; what moves is which seat
     * index lands where. The ENGINE's numbering is untouched, and that is the whole of
     * the fix — the engine is the truth about who acts next, and only the picture was
     * reading it backwards.
     *
     * ONE THING IS NOT A PURE MIRROR, and it is worth stating rather than glossing.
     * The board band below is skipped by walking FORWARD (`k + 1`), so a seat that
     * lands inside it is nudged along the direction of travel — which has just been
     * reversed. On the two top-down rings there is no band at all (`board: null`), so
     * they mirror exactly. (The classic felt, the only ring with a band, is retired.)
     * ===========================================================================
     */
    const x = r.cx + Math.cos(a) * r.rx;
    const y = r.cy + Math.sin(a) * r.ry;
    pts.push({ x, y, ok: !r.board || Math.abs(y - r.board.centre) > r.board.half });
  }

  let cum = 0;
  const run: number[] = [];
  for (let k = 0; k < N; k++) {
    const p = pts[k]!, q = pts[(k + 1) % N]!;
    // y is a percentage of a taller axis, so scale it before measuring distance.
    if (p.ok && q.ok) cum += Math.hypot(q.x - p.x, (q.y - p.y) * r.aspect);
    run.push(cum);
  }

  /**
   * Weighting for the two gaps either side of the hero.
   *
   * Currently 1.0 — even spacing. It is kept as a named constant because it was
   * tried at 1.35 to give the taller hero seat room, and that FAILED: widening two
   * gaps steals from the other seven, and the collision simply moved to the sides.
   * The hero's height was tightened in CSS instead. Left here so the next person to
   * have the same idea can see it was measured and rejected.
   */
  const HERO_GAP = r.heroGap;
  const weightSum = 2 * HERO_GAP + (count - 2);

  const out: SeatBox[] = new Array(count);
  for (let i = 0; i < count; i++) {
    /*
     * Offset so the hero lands at the bottom, the rest going clockwise from there.
     *
     * This line was always right and the walk above was not: `seat` counts up in the
     * engine's own order, so seat 1 after the hero is the seat that acts after the
     * hero, and it takes the first point the walk offers. Phase 5 turned the walk
     * round; nothing here had to change.
     */
    const seat = (i - hero + count) % count;
    const step = seat === 0 ? 0 : (HERO_GAP + (seat - 1)) / weightSum;
    const target = step * cum;
    let k = run.findIndex((v) => v >= target);
    if (k < 0) k = 0;
    let guard = 0;
    while (!pts[k]!.ok && guard++ < N) k = (k + 1) % N;
    const { x, y } = pts[k]!;
    out[i] = {
      x, y,
      /**
       * Where this seat's chips go: partway in toward the pot, on the same bearing.
       * Derived from the seat rather than from its own ellipse so the two can never
       * drift into disagreeing about which way a seat is.
       */
      betX: r.cx + (x - r.cx) * r.betPull.x,
      betY: r.cy + (y - r.cy) * r.betPull.y,
    };
  }
  cache.set(key, out);
  return out;
}

export const cardName = (c: number): { rank: string; suit: number } =>
  ({ rank: RANKS[c >> 2]!, suit: c & 3 });

const SUIT_CHAR = ["♣", "♦", "♥", "♠"];
export const suitChar = (s: number) => SUIT_CHAR[s]!;
export const suitIsRed = (s: number) => s === 1 || s === 2;
export const suitLabel = (s: number) => ["clubs", "diamonds", "hearts", "spades"][s]!;
export const cardLabel = (c: number) => `${RANKS[c >> 2]} of ${suitLabel(c & 3)}`;
void SUITS;

/** Chips, formatted the way a table shows them: no decimals unless there are any. */
export function chips(v: number): string {
  if (!Number.isFinite(v)) return "—";
  const r = Math.round(v * 100) / 100;
  return Math.abs(r % 1) < 1e-9 ? r.toFixed(0) : r.toFixed(2);
}

export interface BetRange {
  /** Smallest legal total-this-street a raise may go to. */
  min: number;
  /** Largest, which is always the whole stack. */
  max: number;
  /** Chips it costs from here, for each of those. */
  minCost: number;
  maxCost: number;
  /** Whether raising is legal at all right now. */
  canRaise: boolean;
  kind: "bet" | "raise";
}

/**
 * The slider's ends, derived from the engine's own legal actions rather than
 * recalculated.
 *
 * Recalculating is how a client and a server end up disagreeing about the minimum
 * raise, and the way that shows up is a player clicking a button and being told their
 * action was illegal — which reads as the game being broken, because it is.
 */
export function betRange(legal: Action[], committed: number, stack: number): BetRange {
  const raise = legal.find((a) => a.kind === "bet" || a.kind === "raise");
  const allIn = legal.find((a) => a.kind === "allin");
  const max = committed + stack;
  if (!raise && !allIn) {
    return { min: 0, max: 0, minCost: 0, maxCost: 0, canRaise: false, kind: "bet" };
  }
  const min = raise?.to ?? max;
  return {
    min, max,
    minCost: Math.max(0, min - committed),
    maxCost: Math.max(0, max - committed),
    canRaise: true,
    kind: raise?.kind === "raise" ? "raise" : "bet",
  };
}

/**
 * Pot-fraction shortcuts, clamped into the legal range.
 *
 * "Half pot" means half of the pot AFTER calling, which is what every client means by
 * it and what makes the arithmetic a player does in their head come out right.
 */
export function sizingShortcuts(
  pot: number, toCall: number, committed: number, range: BetRange, chipSize: number
): { label: string; to: number }[] {
  if (!range.canRaise) return [];
  const base: [string, number][] = [["⅓", 1 / 3], ["½", 0.5], ["¾", 0.75], ["pot", 1]];
  const out: { label: string; to: number }[] = [];
  const seen = new Set<number>();
  for (const [label, frac] of base) {
    const to = legalRaiseTo(committed + toCall + (pot + toCall) * frac, range, chipSize);
    if (seen.has(to)) continue;
    seen.add(to);
    out.push({ label, to });
  }
  // The all-in shortcut is the raw maximum, never rounded down: rounding it left the
  // player with a dust stack and an open decision on the next street, after they had
  // clicked a button labelled "all in".
  if (!seen.has(range.max)) out.push({ label: "all in", to: range.max });
  return out;
}

/**
 * Round to a whole chip, in a stated direction.
 *
 * Direction matters because rounding and clamping do not commute. The first version
 * clamped into the legal range and THEN rounded to the nearest chip, so a minimum
 * raise that was not a chip multiple — which happens on any table with an ante —
 * rounded back below the minimum and the engine rejected it. On a 2/4 table with a 5%
 * ante that was every single min-raise: the player could not raise at all, and the
 * screen said "That move was not legal."
 *
 * So: ceil at the bottom of the range, floor at the top, nearest in between.
 */
export function toChip(v: number, chipSize: number, dir: "up" | "down" | "near" = "near"): number {
  if (!(chipSize > 0)) return v;
  const units = v / chipSize;
  const rounded = dir === "up" ? Math.ceil(units - 1e-9)
    : dir === "down" ? Math.floor(units + 1e-9)
    : Math.round(units);
  return rounded * chipSize;
}

/**
 * The smallest amount the HERO's own controls may move by.
 *
 * ==================== PHASE 6, BOX 0(2): NO 470.50 ON A FELT ====================
 * `phase5-table-live-phone.jpg` showed stacks of 470.50 and 131.50 and a pot to match
 * at a 1/2 cash table. Buster Bucks are whole — the wallet is integers and the Phase 3
 * review settled it — and the half came from the table's own chip unit, which is a
 * QUARTER of a big blind (`Play.tsx`, and 0.5 at a blind of 2). The bots never made
 * one: they size in half-big-blinds. Every fraction on that felt was the player's own
 * slider or a pot-fraction pill.
 *
 * The table's chip unit stays a quarter blind, because that is the granularity the
 * engine deals and prices at and it is not this phase's to change. What changes is
 * what the BAR is allowed to ask for:
 *
 *   - a CASH table is denominated in Bucks, and a Buck does not divide, so the unit
 *     is one;
 *   - a TOURNAMENT is denominated in chips that were never Bucks, so the unit is the
 *     table's own chip unit — and never a fraction of a chip, because "half a chip"
 *     is not a thing a dealer can make change for either.
 *
 * Rounding happens BEFORE the clamp, in `legalRaiseTo`, which is the order that keeps
 * a min-raise legal on a table with an ante (the note on `toChip` has the scar).
 * ===============================================================================
 */
export function sizingUnit(format: "cash" | "tournament", chipSize: number): number {
  if (format === "cash") return 1;
  return Number.isInteger(chipSize) && chipSize >= 1 ? chipSize : 1;
}

/** Nearest whole chip. Kept for callers that genuinely want nearest. */
export function clampToChip(v: number, chipSize: number): number {
  return toChip(v, chipSize, "near");
}

/**
 * The legal amount closest to what the player asked for.
 *
 * One place decides this so the slider, the shortcuts and the confirm button cannot
 * disagree — and so nothing can construct a raise the engine will refuse.
 */
export function legalRaiseTo(want: number, range: BetRange, chipSize: number): number {
  if (want >= range.max) return range.max;
  const snapped = toChip(want, chipSize, "near");
  if (snapped >= range.max) return range.max;
  if (snapped < range.min) return Math.min(range.max, toChip(range.min, chipSize, "up"));
  return snapped;
}

/**
 * Position names for a table of this size, indexed from the button.
 *
 * Slicing the nine-handed list from the front is wrong for every short table: it
 * labelled the two seats immediately right of a six-max button "UTG1" and "UTG2" when
 * they are the hijack and the cutoff. That is not a cosmetic error — these names
 * appear on the felt, in the turn prompt and on every line of the hand history, and
 * telling a player they opened under the gun when they were on the cutoff teaches
 * them the wrong range.
 *
 * The blinds are fixed at the front; the first seat after the big blind is always
 * under the gun; the seat before the button is always the cutoff; and the middle
 * fills in from the late end, which is how the names are actually used.
 */
export function positionNames(count: number): string[] {
  if (count === 2) return ["BTN", "BB"];
  const head = ["BTN", "SB", "BB"];
  const middle = count - 3;
  if (middle <= 0) return head.slice(0, count);
  const tail = ["UTG1", "UTG2", "LJ", "HJ", "CO"];
  return [...head, "UTG", ...tail.slice(tail.length - (middle - 1))];
}

/** The position label for a seat, given where the button is. */
export function positionOf(seat: number, button: number, count: number): string {
  const names = positionNames(count);
  return names[(seat - button + count) % count] ?? "";
}

/* ------------------------------------------------------------------- seeds --- */

/**
 * A fresh 256-bit shuffle seed, as hex.
 *
 * A 52-card deck has 52! orderings, which needs 226 bits to reach; anything smaller
 * makes most decks impossible before a card is dealt. The first version of this used a
 * 32-bit value derived from the clock, which is both far too small and — because it
 * was a module constant — the same for every table in a page load.
 */
export function freshSeed(): string {
  return toHex(randomSeed());
}

/**
 * Turn whatever the player typed into a usable seed.
 *
 * Sixty-four hex characters is a seed already, and is what the game prints after a
 * session so an exact one can be pasted back. Anything else is treated as a LABEL and
 * hashed into one — "friday night" is a perfectly good way to ask for a repeatable
 * table, and it carries exactly as much unpredictability as the words do, which does
 * not matter because reproducing is the thing being asked for.
 */
export function normaliseSeed(text: string): string {
  const clean = text.trim().toLowerCase();
  if (/^[0-9a-f]{64}$/.test(clean)) return clean;
  return toHex(seedFromLabel(clean));
}

/**
 * The seed a table should actually use.
 *
 * Random every time, unless the player deliberately asked for one — which is the whole
 * point of exposing the field. A new deck is the default; reproducing a session is the
 * opt-in.
 */
export function seedFor(s: { seed: string; seedLocked?: boolean }): string {
  if (!s.seedLocked || !s.seed) return freshSeed();
  return normaliseSeed(s.seed);
}
